import { LinkedContextState } from '@context/LinkedItemZoneContext'
import { Textures } from '@types'
import handleGroupClick from './handleGroupClick'
import { useAppStore } from '@store'
import { getProxyUrl } from '@proxies/index'

export async function handleZoneClick(
  context: NonNullable<LinkedContextState>,
  linkedTextureID: Textures[number],
  isChild = false,
) {
  try {
    let preCenterInfo
    let response

    if (isChild || !linkedTextureID.linkedTextureID) {
      const currentObjectID = useAppStore.getState().currentObjectID
      const currentItem = useAppStore.getState().items.get(currentObjectID)

      if (!currentItem || !currentItem.idUnidades) {
        console.warn('No se encontró idUnidades para el elemento hijo.')
        return
      }
      const idUnidades = currentItem.idUnidades

      response = await fetch(
        getProxyUrl(`api/acabados/getelementosenlazadosunidadacabados/${idUnidades}`),
        {
          method: 'GET',
          headers: {
            'Accept-Language': 'ES',
            'Content-Type': 'application/json',
            Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
          },
        },
      )
    } else {
      response = await fetch(
        getProxyUrl(
          `api/Acabados/getacabadozonamodeloacabadoinfo/${linkedTextureID.linkedTextureID}`,
        ),
        {
          method: 'GET',
          headers: {
            'Accept-Language': 'ES',
            'Content-Type': 'application/json',
            Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
          },
        },
      )
    }

    // Caso 204 No Content
    if (response.status === 204) {
      context.setAcabado(null)
      context.setModelo(null)
      context.setSelectedTexture(linkedTextureID)
      context.setCenterInfo([])
      context.setIsModel(false)
      return
    }

    preCenterInfo = await response.json()
    const data = preCenterInfo?.data || preCenterInfo

    if (!data || !Array.isArray(data)) {
      context.setCenterInfo([])
      return
    }

    const normalize = (str: any) =>
      String(str || '')
        .toLowerCase()
        .trim()

    let currElement = data.find((el) => {
      const zonaNormalized = normalize(el.zona)
      const displayNameNormalized = normalize(linkedTextureID.zoneDisplayName)
      const zoneNameNormalized = normalize(linkedTextureID.zone)

      const isDecoArmazon = displayNameNormalized === 'armazon' || zoneNameNormalized === 'armazon'

      let matches =
        zonaNormalized.includes(displayNameNormalized) ||
        zonaNormalized.includes(zoneNameNormalized) ||
        displayNameNormalized.includes(zonaNormalized) ||
        zoneNameNormalized.includes(zonaNormalized) ||
        zonaNormalized === 'cajon1'

      if (isDecoArmazon && !matches) {
        matches =
          zonaNormalized.includes('puerta') ||
          zonaNormalized.includes('door') ||
          'puerta'.includes(zonaNormalized)
      }

      return matches
    })

    // Fallback específico para Atemporal: zona principal siempre es Tela.
    if (!currElement) {
      currElement = data.find((el) => normalize(el.zona).includes('tela'))
    }

    if (!currElement) {
      context.setCenterInfo([])
      return
    }

    let currentAcabado = currElement.acabados.find((el) => el.acabado === linkedTextureID.acabado)
    if (!currentAcabado) {
      currentAcabado = currElement.acabados[0]
    }

    // Orden específico de acabados
    const acabadoOrder = [
      'MATERIA',
      'TMATT',
      'CRYSTAL',
      'STRATI',
      'MATT',
      'NATURA',
      'ECOVINTAGE',
      'BLANCOS',
      'MADERAS',
      'BASICOS',
      'LINEAL',
      'RUSTIK',
      'RUSTIK2',
      'LARA',
    ]

    const processedAcabados = currElement.acabados
      .map((acabado) => ({
        ...acabado,
        idAcabadoUnidadModeloAcabado:
          acabado.idAcabadoUnidad || acabado.idAcabadoUnidadModeloAcabado,
      }))
      .sort((a, b) => {
        const indexA = acabadoOrder.findIndex((order) => order.trim() === a.acabado?.trim())
        const indexB = acabadoOrder.findIndex((order) => order.trim() === b.acabado?.trim())

        // Si ambos están en el orden, ordenar por índice
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }
        // Si solo A está en el orden, A va primero
        if (indexA !== -1) return -1
        // Si solo B está en el orden, B va primero
        if (indexB !== -1) return 1
        // Si ninguno está en el orden, mantener orden original
        return 0
      })

    const targetAcabadoId =
      currentAcabado.idAcabadoUnidad || currentAcabado.idAcabadoUnidadModeloAcabado

    handleGroupClick(targetAcabadoId, context, currentAcabado.acabado)

    try {
      const texturasResp = await fetch(getProxyUrl(`api/Acabados/gettexturas/${targetAcabadoId}`), {
        method: 'GET',
        headers: {
          'Accept-Language': 'ES',
          'Content-Type': 'application/json',
          Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
        },
      })
      const texturasJson = await texturasResp.json()
      const texturas = Array.isArray(texturasJson?.data) ? texturasJson.data : texturasJson

      const preservedName = linkedTextureID.name || ''
      const preservedDescription =
        linkedTextureID.description || linkedTextureID.descripcion || preservedName

      // Primero buscar una coincidencia exacta por nombre (la textura realmente aplicada)
      const exactMatch = (Array.isArray(texturas) ? texturas : []).find((t) => {
        if (!preservedName) return false
        const currName = preservedName.toLowerCase().trim()
        return (
          (t?.nombre || '').toLowerCase().trim() === currName ||
          (t?.descripcion || '').toLowerCase().trim() === currName
        )
      })

      // Si no hay coincidencia exacta, buscar por coincidencia parcial
      const matchByName = (t) => {
        const currName = (linkedTextureID.name || linkedTextureID.acabado || '').toLowerCase()
        return (
          (t?.nombre || '').toLowerCase() === currName ||
          (t?.descripcion || '').toLowerCase() === currName ||
          (t?.nombre || '').toLowerCase().includes(currName) ||
          (t?.descripcion || '').toLowerCase().includes(currName)
        )
      }
      const selected = exactMatch || (Array.isArray(texturas) ? texturas : []).find(matchByName)

      if (selected) {
        // Usar la textura encontrada pero preservar name y description de linkedTextureID
        context.setSelectedTexture({
          ...selected,
          name: preservedName || selected.nombre || selected.name,
          descripcion: preservedDescription || selected.descripcion || preservedName,
          description: preservedDescription || selected.descripcion || preservedName,
          zoneName: linkedTextureID.zoneName,
          zoneDisplayName: linkedTextureID.zoneDisplayName,
        })
      }
    } catch (e) {
      // noop
    }

    context.setCenterInfo(processedAcabados)
    context.setAcabado(linkedTextureID.acabado)

    if (!context.selectedTexture || !context.selectedTexture.idTexturas) {
      context.setSelectedTexture(linkedTextureID)
    }

    context.setModelo(currElement.modelo)
    context.setIsModel(false)
  } catch (error) {
    // Evitar spam en consola; degradar con salida silenciosa.
    context.setCenterInfo([])
    context.setIsModel(false)
  }
}
